// Notification Tools
// Send notifications via notification_worker (email, telegram, phone)

import { ToolCategory } from '../../ToolCategory.js'
import { validateRequired } from '../../utils/validation.js'
import { forwardCallByName } from 'workers-common'

/**
 * Create Notification tools with getWorkerClient function
 * Tools dynamically get workerClient on each call (not cached)
 * @param {Object} options - Options object
 * @param {Function} options.getWorkerClient - Function to get WorkerClient instance
 * @returns {Object} Notification tools
 */
export function createNotificationTools({ getWorkerClient }) {
  /**
   * Send notification via notification_worker
   * Supports three types: email, telegram, phone
   * Uses callByName to resolve worker name via namespace service
   * @param {Object} args - Notification parameters
   * @param {string} args.type - Notification type: 'email', 'telegram', 'phone'
   * @param {Object} args.params - Type-specific parameters
   * @returns {Promise<Object>} Result from notification_worker
   */
  const sendNotification = async (ctx, args) => {
    validateRequired(args, ['type', 'params'], 'sendNotification')
    const { type, params } = args

    if (typeof params !== 'object') {
      throw new Error('sendNotification: params must be an object')
    }

    // Get workerClient dynamically (not cached)
    const workerClient = getWorkerClient()

    // Map type to notification_worker method and validate params
    let method
    let rpcParams

    switch (type) {
      case 'email':
        method = 'notifyByEmail'
        validateRequired(params, ['toEmailAddress', 'subject', 'msg'], 'Email notification')
        rpcParams = {
          toEmailAddress: params.toEmailAddress,
          toName: params.toName || '',
          subject: params.subject,
          msg: params.msg,
          priority: params.priority || 'normal',
        }
        break

      case 'telegram':
        method = 'notifyByTgbot'
        validateRequired(params, ['botId', 'chatId', 'text'], 'Telegram notification')
        rpcParams = {
          botId: params.botId,
          chatId: params.chatId,
          text: params.text,
          parseMode: params.parseMode || 'Markdown',
        }
        break

      case 'phone':
        method = 'notifyByPhoneCall'
        validateRequired(params, ['toPhoneNumber', 'message'], 'Phone notification')
        rpcParams = {
          toPhoneNumber: params.toPhoneNumber,
          message: params.message,
          language: params.language || 'en-US',
          voice: params.voice || 'male',
          maxRetries: params.maxRetries || 1,
        }
        break

      default:
        throw new Error(
          `Unknown notification type: ${type}. Supported types: email, telegram, phone`,
        )
    }

    // Call notification_worker via RPC using forwardCallByName
    // Worker name is resolved via namespace service automatically
    try {
      const result = await forwardCallByName(ctx, workerClient, 'notification-worker', method, [
        rpcParams,
      ])

      return {
        success: true,
        type,
        result,
      }
    } catch (error) {
      throw new Error(`Notification failed: ${error.message}`)
    }
  }

  return { sendNotification }
}

// Tool definition for LLM
export const sendNotificationDefinition = {
  name: 'sendNotification',
  description: `Send notifications to users via different channels (email, telegram, phone call).

**Usage Examples**:

1. Send Email:
   type: "email"
   params: {
     toEmailAddress: "user@example.com",
     toName: "John Doe",
     subject: "Important Update",
     msg: "Your task is complete",
     priority: "high"  // optional: normal, high, urgent
   }

2. Send Telegram Message:
   type: "telegram"
   params: {
     botId: "bot123",
     chatId: "chat456",
     text: "Hello from MyAgent!",
     parseMode: "Markdown"  // optional: Markdown, HTML
   }

3. Make Phone Call:
   type: "phone"
   params: {
     toPhoneNumber: "+1234567890",
     message: "This is an important alert",
     language: "en-US",  // optional: en-US, zh-CN, etc.
     voice: "female",     // optional: male, female
     maxRetries: 2        // optional: retry count
   }

Use this tool when the user explicitly requests to send notifications or alerts.`,
  parameters: {
    type: 'object',
    properties: {
      type: {
        type: 'string',
        enum: ['email', 'telegram', 'phone'],
        description: 'Notification type: email, telegram, or phone',
      },
      params: {
        type: 'object',
        description:
          'Type-specific parameters. See examples in tool description for required fields for each type.',
      },
    },
    required: ['type', 'params'],
  },
}

/**
 * Create Notification/Communication category for ToolCategory system
 * @param {Object} options - Options object
 * @param {Function} options.getWorkerClient - Function to get WorkerClient instance
 * @returns {ToolCategory} Communication tool category
 */
export function createNotificationCategory({ getWorkerClient }) {
  const notificationTools = createNotificationTools({ getWorkerClient })

  return new ToolCategory({
    id: 'communication',
    description: `Send notifications via email, Telegram, or phone`,
    tools: [
      {
        definition: sendNotificationDefinition,
        implementation: notificationTools.sendNotification,
      },
    ],
    subCategories: [],
  })
}
